/**
 * The journey view: what a settled item's stages actually record (YAG-372).
 *
 * Replaces the "the ask" block in the pane once an item is done or closed. Each
 * row carries who acted (a worker's chip, or a real person's name), when, and
 * the gate marker: it waited on an editor, or it ran without waiting, or it is
 * the merge row that never waited on a worker because merging is not delegated.
 *
 * `buildJourney` does the assembly from real records; this file only draws it.
 */
import type { WorkItem, WorkItemActivityDto, WorkItemGate } from "@yagni/shared";
import { Pane } from "../ui/index.js";
import { buildJourney, journeyTime } from "./journeyRows.js";
import { WorkerChip } from "./WorkerChip.js";
import { JOURNEY_EMPTY, JOURNEY_FROM_CHAT, JOURNEY_LABEL, JOURNEY_YOU } from "./workCopy.js";
import { PIPELINE_STAGE_LABEL } from "./workStage.js";
import type { WorkerLookup } from "./useWorkerLookup.js";

export function Journey({
  item,
  activity,
  gates,
  workerOf,
  label = JOURNEY_LABEL,
}: {
  item: WorkItem;
  activity: WorkItemActivityDto[];
  gates: WorkItemGate[];
  workerOf?: WorkerLookup;
  /** The heading. The case file's Record calls it the timeline. */
  label?: string;
}) {
  const rows = buildJourney({ item, activity, gates });

  return (
    <section className="wk-journey cf-section" data-testid="work-journey">
      <div className="cf-sect">{label}</div>
      {/* Settled history wears print, like every other committed card. */}
      <Pane material="printed" className="cf-card">
        {rows.length === 0 ? (
          <p className="wk-journey-empty" data-testid="work-journey-empty">
            {JOURNEY_EMPTY}
          </p>
        ) : (
          rows.map((row) => {
            const worker = workerOf?.(row.personaSlug) ?? null;
            return (
              <div className="wk-journey-row" key={row.key} data-testid={`journey-${row.key}`}>
                <div className="wk-journey-head">
                  {worker ? (
                    <WorkerChip worker={worker} testId={`journey-worker-${row.key}`} />
                  ) : (
                    // A person's own act. Their name when the record has one; the
                    // plain "you" only when nothing named an actor at all.
                    <span className="wk-journey-actor">{row.actorName ?? JOURNEY_YOU}</span>
                  )}
                  {row.fromChat && (
                    <span className="wk-journey-via" data-testid={`journey-via-${row.key}`}>
                      {JOURNEY_FROM_CHAT}
                    </span>
                  )}
                  <span className="wk-journey-stage">{PIPELINE_STAGE_LABEL[row.stage]}</span>
                  <span className="wk-journey-time">{journeyTime(row.at)}</span>
                  {row.marker && <span className="wk-journey-marker">{row.marker}</span>}
                </div>
                <div className="wk-journey-title">{row.title}</div>
                {/* Which standing job this was, when a Job staged it. */}
                {row.jobLine && (
                  <div className="wk-journey-job" data-testid={`journey-job-${row.key}`}>
                    {row.jobLine}
                  </div>
                )}
                {row.stamp && (
                  <div className="wk-journey-stamp">
                    <span className="wk-stamp">{row.stamp.word}</span>
                    <span className="wk-journey-by">{row.stamp.byLine}</span>
                  </div>
                )}
              </div>
            );
          })
        )}
      </Pane>
    </section>
  );
}
